import {createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode} from 'react';
import {CheckCircle2, Info, TriangleAlert} from 'lucide-react';

/**
 * The one place the product talks back.
 *
 * A marketplace does a lot of small, invisible work on the visitor's behalf —
 * saving a listing, sending a message, publishing a post. Each of those needs a
 * receipt, and a receipt that covers the screen is worse than none: the toast
 * sits above the dock, lasts three seconds, and never blocks a click.
 */
type ToastTone = 'ok' | 'info' | 'warn';

interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
}

interface ToastValue {
  notify: (text: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}

const ICON: Record<ToastTone, ReactNode> = {
  info: <Info size={16} />,
  ok: <CheckCircle2 size={16} />,
  warn: <TriangleAlert size={16} />,
};

const TONE: Record<ToastTone, string> = {
  info: 'text-ink',
  ok: 'text-accent',
  warn: 'text-[#a3262b]',
};

export function ToastProvider({children}: {children: ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seqRef = useRef(0);

  const notify = useCallback((text: string, tone: ToastTone = 'ok') => {
    seqRef.current += 1;
    const id = seqRef.current;
    setToasts((prev) => [...prev.slice(-2), {id, text, tone}]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  }, []);

  const value = useMemo<ToastValue>(() => ({notify}), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-28 z-[9800] flex flex-col items-center gap-2 px-4">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            className="surface flex max-w-md items-center gap-2.5 rounded-full py-2.5 pl-3.5 pr-4 text-[13px] font-semibold text-ink"
          >
            <span className={TONE[toast.tone]}>{ICON[toast.tone]}</span>
            <span className="min-w-0 truncate">{toast.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
